import React from 'react';
import {
  isPricingHash,
  matchSelfCheckinHash,
  matchSharedReportPath,
} from './appRoutePolicy';
import useServiceHash from './hooks/useServiceHash';
import { createLazyRoute } from './lib/lazyRoute';
import LazyRouteRecoveryBoundary from './routes/LazyRouteRecoveryBoundary';
import {
  buildPrimaryLayer365Url,
  isLayer365LegacyHostname,
  isLegacyRouteRedirectRequired,
} from './lib/layer365/domainCutover';

const [TrainerApp, preloadTrainerApp] = createLazyRoute(() => import('./TrainerApp'));
const [PricingPage, preloadPricingPage] = createLazyRoute(
  () => import('./components/pricing/PricingPage'),
);
const [SelfCheckinShell, preloadSelfCheckinShell] = createLazyRoute(
  () => import('./components/self/SelfCheckinShell'),
);
const [PublicSharedReportRoute, preloadPublicSharedReportRoute] = createLazyRoute(
  () => import('./routes/PublicSharedReportRoute'),
);
const [Layer365EntryApp, preloadLayer365EntryApp] = createLazyRoute(
  () => import('./components/layer365/Layer365EntryApp'),
);

function FullscreenStatus({ children }) {
  return (
    <div
      role="status"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ds-bg)',
        fontFamily: 'var(--ds-font-sans)',
      }}
    >
      <div
        style={{
          color: 'var(--ds-text-2)',
          fontSize: 'var(--ds-fs-body)',
          fontWeight: 500,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function AppLoadingScreen() {
  return <FullscreenStatus>로딩 중…</FullscreenStatus>;
}

function SharedRouteLoadingScreen() {
  return <FullscreenStatus>회원 전용 화면을 준비하는 중…</FullscreenStatus>;
}

function retryLazyRoute(preload) {
  preload.reset();
  return preload();
}

export default function App() {
  const {
    routeHash,
    routePathname,
    consumeRouteHash,
  } = useServiceHash();
  const selfRoute = matchSelfCheckinHash(routeHash);
  const pathname = routePathname || (
    typeof window === 'undefined' ? '/' : window.location.pathname
  );
  const sharedRoute = matchSharedReportPath(pathname);
  const legacySurface = typeof window !== 'undefined'
    && isLayer365LegacyHostname(window.location.hostname);
  const [legacyAuthorized, setLegacyAuthorized] = React.useState(false);
  const [legacySessionRevision, setLegacySessionRevision] = React.useState(0);
  const handleLegacyAuthorized = React.useCallback(() => {
    setLegacySessionRevision((revision) => revision + 1);
    setLegacyAuthorized(true);
  }, []);
  const handleLegacyAuthenticationRequired = React.useCallback(
    () => setLegacyAuthorized(false),
    [],
  );
  const legacyRedirectActive = legacySurface && isLegacyRouteRedirectRequired(routeHash);
  const publicServiceActive = isPricingHash(routeHash) || !!selfRoute || !!sharedRoute;
  const layer365Active = (
    !publicServiceActive
    && !legacyRedirectActive
    && (!legacySurface || !legacyAuthorized)
  );
  const trainerActive = (
    !publicServiceActive
    && legacySurface
    && legacyAuthorized
    && !legacyRedirectActive
  );
  const [trainerActivated, setTrainerActivated] = React.useState(() => trainerActive);
  const [trainerRouteRevision, setTrainerRouteRevision] = React.useState(0);

  React.useEffect(() => {
    if (!legacyRedirectActive || typeof window === 'undefined') return;
    window.location.replace(buildPrimaryLayer365Url(routeHash));
  }, [legacyRedirectActive, routeHash]);

  React.useEffect(() => {
    if (!trainerActive) return;
    setTrainerActivated(true);
    if (preloadTrainerApp.reset()) {
      setTrainerRouteRevision((revision) => revision + 1);
    }
  }, [trainerActive]);

  let publicContent = null;
  let publicFallback = <AppLoadingScreen />;
  let publicRouteKey;
  let publicRouteType;
  let publicReloadGuardKey;
  let publicPreload;

  if (isPricingHash(routeHash)) {
    publicRouteKey = 'pricing';
    publicRouteType = 'pricing';
    publicReloadGuardKey = 'pricing';
    publicPreload = preloadPricingPage;
    publicContent = (
      <PricingPage
        onLogin={() => { window.location.hash = ''; }}
        onSignup={() => { window.location.hash = ''; }}
        visualMode="finalFreeze"
      />
    );
  } else if (selfRoute) {
    publicRouteKey = `self:${selfRoute.token}`;
    publicRouteType = 'self';
    publicReloadGuardKey = 'self';
    publicPreload = preloadSelfCheckinShell;
    publicContent = (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--ds-bg)',
          fontFamily: 'var(--ds-font-sans)',
        }}
      >
        <SelfCheckinShell
          key={selfRoute.token}
          token={selfRoute.token}
          visualMode="finalFreeze"
          onClose={() => { window.location.hash = ''; }}
        />
      </div>
    );
  } else if (sharedRoute) {
    publicRouteKey = `share:${sharedRoute.token}`;
    publicRouteType = 'share';
    publicReloadGuardKey = 'share';
    publicPreload = preloadPublicSharedReportRoute;
    publicFallback = <SharedRouteLoadingScreen />;
    publicContent = (
      <PublicSharedReportRoute
        key={sharedRoute.token}
        token={sharedRoute.token}
        initialWeek={sharedRoute.weekNumber}
      />
    );
  }

  const trainerFallback = trainerActive ? <AppLoadingScreen /> : null;
  const renderTrainerApp = trainerActivated || trainerActive;

  return (
    <>
      {renderTrainerApp && (
        <LazyRouteRecoveryBoundary
          fallback={trainerFallback}
          key={`trainer-session:${legacySessionRevision}`}
          maxInPlaceRetries={1}
          onRetry={trainerActive ? () => retryLazyRoute(preloadTrainerApp) : undefined}
          reloadGuardKey="trainer"
          reloadOnChunkError={trainerActive}
          resetKey={`trainer:${trainerRouteRevision}`}
        >
          <React.Suspense fallback={trainerFallback}>
            <TrainerApp
              active={trainerActive}
              routeHash={trainerActive ? routeHash : ''}
              consumeRouteHash={consumeRouteHash}
              onAuthenticationRequired={legacySurface
                ? handleLegacyAuthenticationRequired
                : undefined}
            />
          </React.Suspense>
        </LazyRouteRecoveryBoundary>
      )}
      {publicContent && (
        <LazyRouteRecoveryBoundary
          key={publicRouteKey}
          fallback={publicFallback}
          maxInPlaceRetries={1}
          onRetry={() => retryLazyRoute(publicPreload)}
          reloadGuardKey={publicReloadGuardKey}
          reloadOnChunkError
          resetKey={publicRouteType}
        >
          <React.Suspense fallback={publicFallback}>
            {publicContent}
          </React.Suspense>
        </LazyRouteRecoveryBoundary>
      )}
      {layer365Active && (
        <LazyRouteRecoveryBoundary
          fallback={<AppLoadingScreen />}
          maxInPlaceRetries={1}
          onRetry={() => retryLazyRoute(preloadLayer365EntryApp)}
          reloadGuardKey="layer365-entry"
          reloadOnChunkError
          resetKey="layer365-entry"
        >
          <React.Suspense fallback={<AppLoadingScreen />}>
            <Layer365EntryApp
              admissionMode={legacySurface ? 'legacy' : 'primary'}
              routeHash={routeHash}
              consumeRouteHash={consumeRouteHash}
              onLegacyAuthorized={handleLegacyAuthorized}
            />
          </React.Suspense>
        </LazyRouteRecoveryBoundary>
      )}
      {legacyRedirectActive && <AppLoadingScreen />}
    </>
  );
}
